import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../shared/contexts/AuthContext';
import LandingPage from './marketing/components/LandingPage';
import { Spinner } from '../shared/components/common/Spinner';
import { profileApi } from '../shared/api/profileApi';

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, error } = useAuth();
  const location = useLocation();
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState<boolean | null>(null);

  useEffect(() => {
    async function checkOnboarding() {
      if (user) {
        try {
          const profile = await profileApi.getProfile();
          setHasCompletedOnboarding(profile.has_completed_onboarding);
        } catch (err) {
          console.error('Failed to check onboarding status:', err);
          setHasCompletedOnboarding(false);
        }
      }
    }
    checkOnboarding();
  }, [user]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Error</h2>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return location.pathname === '/' 
      ? <LandingPage /> 
      : <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show loading while checking onboarding status
  if (hasCompletedOnboarding === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner variant="border" size="lg" className="text-gray-900" />
      </div>
    );
  }

  // Redirect to onboarding if not completed (except if already on onboarding page)
  if (!hasCompletedOnboarding && location.pathname !== '/onboarding' && !location.state?.hasCompletedOnboarding) {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}